import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, takeUntil } from 'rxjs';

import { setCurrencies } from '@app/state/global/global.actions';
import { Permissions } from '@app/core/guard/Permissions';
import { LocalizationApiService } from '@services/api/localization-api.service';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { UtilsService } from '@services/helpers/utils.service';
import { InternationalCurrencyService } from '@services/http/internacionalCurrency/currency.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { VaultBaseComponent } from '@shared/components/base/vault-base.component';
import { BankContactsModalService } from '@shared/services/bank-contacts-modal.service';
import { SideMenuService } from '@shared/services/side-menu.service';
import { selectLocalizationSettings } from '@state/auth/auth.selectors';
import { UserLocalizationSetting } from '@state/auth/auth.state';
import { AppState } from '@state/index';
import { ModalBankContactsComponent } from './market/banking-relationship/components/modal-bank-contacts/modal-bank-contacts.component';
import { OnboardingNavigationService } from '@src/app/features/onboarding/services/onboarding-navigation.service';
import { OnboardingSelectCompanyModalComponent } from '@src/app/features/onboarding/components/onboarding-select-company-modal/onboarding-select-company-modal.component';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false,
})
export class DashboardComponent extends VaultBaseComponent implements OnInit, OnDestroy {
  @ViewChild('bankContactsDialog') bankContactsDialogRef: ModalBankContactsComponent | null = null;
  @ViewChild('onboardingSelectCompanyDialog')
  onboardingSelectCompanyDialogRef: OnboardingSelectCompanyModalComponent | null = null;

  /** Permissão que decide se o modal de contatos bancários chega a ser montado no shell. */
  readonly bankContactsPermission = Permissions.BankRelationship.LIST;

  currentServerDate$: Observable<Date | null>;
  currentLocalizationSettings$: Observable<UserLocalizationSetting>;
  isMobile: boolean = false;
  isMenusOpen: boolean = false;
  isMenuPinned: boolean = false;
  isTopMenuLayout = false;
  isSidebarVisible = true;
  private destroy$ = new Subject<void>();
  constructor(
    private utilsService: UtilsService,
    private localizationApiService: LocalizationApiService,
    private readonly internationalCurrencyService: InternationalCurrencyService,
    private readonly formErrorHandlerService: FormErrorHandlerService,
    private readonly bankContactsModalService: BankContactsModalService,
    private readonly onboardingNavigationService: OnboardingNavigationService,
    private readonly permissionsService: PermissionsService,
    private sideMenuService: SideMenuService,
    store: Store<AppState>,
    translateService: TranslateService
  ) {
    super(translateService, store);

    this.currentServerDate$ = this.localizationApiService.currentServerDate();
    this.currentLocalizationSettings$ = store.select(selectLocalizationSettings);
  }

  ngOnInit(): void {
    this.getInternationalCurrencies();
    this.subscribeToMenuState();
    this.subscribeToBankContactsModal();
    this.subscribeToOnboardingModal();
  }

  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.documentElement.style.removeProperty('--dashboard-header-offset');
    super.ngOnDestroy();
  }

  private subscribeToMenuState(): void {
    this.sideMenuService.isOpen$.pipe(takeUntil(this.destroy$)).subscribe(isOpen => {
      this.isMenusOpen = isOpen;
    });

    this.sideMenuService.isPinned$.pipe(takeUntil(this.destroy$)).subscribe(isPinned => {
      this.isMenuPinned = isPinned;
    });

    this.sideMenuService.isMobile$.pipe(takeUntil(this.destroy$)).subscribe(isMobile => {
      this.isMobile = isMobile;
    });

    this.sideMenuService.menuLayout$.pipe(takeUntil(this.destroy$)).subscribe(layout => {
      this.isTopMenuLayout = layout === 'top';
      document.documentElement.style.setProperty(
        '--dashboard-header-offset',
        layout === 'top' ? '65px' : '0px'
      );
    });

    this.sideMenuService.isSidebarVisible$.pipe(takeUntil(this.destroy$)).subscribe(visible => {
      this.isSidebarVisible = visible;
    });
  }

  private subscribeToBankContactsModal(): void {
    this.bankContactsModalService.openModal$.pipe(takeUntil(this.destroy$)).subscribe(payload => {
      const allowAllCompanies =
        payload.allowAllCompanies ?? (!payload.clientId && !payload.clientIds?.length);
      this.bankContactsDialogRef?.open(
        payload.clientId,
        payload.bankRelationshipId,
        payload.clientIds,
        allowAllCompanies
      );
    });
  }

  private subscribeToOnboardingModal(): void {
    this.onboardingNavigationService.openModal$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ companyIds }) => {
        this.onboardingSelectCompanyDialogRef?.open(companyIds);
      });
  }

  private getInternationalCurrencies(): void {
    this.internationalCurrencyService.getInternationalCurrencies(true).subscribe({
      next: response => this.store?.dispatch(setCurrencies({ currencies: response || [] })),
      error: error =>
        this.formErrorHandlerService.showToast(
          error,
          this.tk.Management.ErrorLoadingInternacionalCurrencies
        ),
    });
  }
}
